// Generado desde design-system/tokens/echarts-theme.ts — no editar; edita el .ts/.tsx y corre build-ds.
/**
 * Temas de ECharts "databunker-light" y "databunker-dark".
 * Se registran UNA vez al arrancar la app (registerDataBunkerThemes) y cada
 * gráfica los usa con <ReactECharts theme="databunker-light" />, o mejor, con
 * el componente <EChart> del sistema, que elige el tema según data-theme.
 *
 * Reglas que el tema ya resuelve (no repetirlas en cada option):
 *  - paleta categórica validada en orden fijo
 *  - tipografía Open Sans, ejes y rejilla recesivos, sin doble eje
 *  - líneas de 2px sin símbolos (aparecen al hover), barras con punta de 4px
 *  - tooltip tipo vidrio (HTML), crosshair en ejes
 */
import * as echarts from 'echarts';
import { CATEGORICAL, CHROME, FONT, SEQUENTIAL_BLUE } from './palette';
function build(mode) {
    const c = CHROME[mode];
    const axisCommon = {
        axisLine: { show: true, lineStyle: { color: c.axis, width: 1 } },
        axisTick: { show: false },
        axisLabel: { color: c.ink3, fontFamily: FONT.sans, fontSize: 11, margin: 10 },
        splitLine: { show: true, lineStyle: { color: c.grid, width: 1 } },
        splitArea: { show: false },
        nameTextStyle: { color: c.ink3, fontFamily: FONT.sans, fontSize: 11 },
    };
    return {
        color: CATEGORICAL[mode],
        backgroundColor: 'transparent',
        textStyle: { fontFamily: FONT.sans, color: c.ink2 },
        title: {
            textStyle: { fontFamily: FONT.display, fontWeight: 500, fontSize: 16, color: c.ink },
            subtextStyle: { fontFamily: FONT.sans, fontSize: 12, color: c.ink3 },
        },
        grid: { left: 8, right: 16, top: 36, bottom: 8, containLabel: true },
        legend: {
            top: 0,
            left: 0,
            icon: 'roundRect',
            itemWidth: 12,
            itemHeight: 4,
            itemGap: 16,
            textStyle: { color: c.ink2, fontFamily: FONT.sans, fontSize: 12 },
            inactiveColor: c.muted,
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: c.tooltipBg,
            borderColor: c.tooltipBorder,
            borderWidth: 1,
            padding: [10, 12],
            textStyle: { color: c.ink, fontFamily: FONT.sans, fontSize: 12 },
            extraCssText: `border-radius:14px;backdrop-filter:blur(14px) saturate(170%);-webkit-backdrop-filter:blur(14px) saturate(170%);box-shadow:${c.tooltipShadow};`,
            axisPointer: {
                type: 'line',
                lineStyle: { color: c.axis, width: 1, type: 'dashed' },
                crossStyle: { color: c.axis },
                label: { backgroundColor: mode === 'light' ? '#062341' : '#EAF2FA', color: mode === 'light' ? '#FFFFFF' : '#03172C', fontFamily: FONT.sans },
            },
        },
        categoryAxis: { ...axisCommon, splitLine: { show: false }, boundaryGap: true },
        valueAxis: { ...axisCommon, axisLine: { show: false } },
        timeAxis: { ...axisCommon, splitLine: { show: false } },
        logAxis: { ...axisCommon, axisLine: { show: false } },
        line: {
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 8,
            showSymbol: false,
            smooth: false,
            emphasis: { focus: 'series', lineStyle: { width: 2.5 } },
        },
        bar: {
            itemStyle: { borderRadius: [4, 4, 0, 0] },
            barMaxWidth: 28,
            emphasis: { focus: 'series' },
        },
        pie: {
            itemStyle: { borderColor: c.surface, borderWidth: 2, borderRadius: 4 },
            label: { color: c.ink2, fontFamily: FONT.sans },
        },
        scatter: { symbolSize: 9, itemStyle: { borderColor: c.surface, borderWidth: 1.5, opacity: 0.9 } },
        heatmap: { itemStyle: { borderColor: c.surface, borderWidth: 2, borderRadius: 3 } },
        gauge: {
            axisLine: { lineStyle: { color: [[1, c.grid]] } },
            detail: { fontFamily: FONT.display, color: c.ink },
        },
        visualMap: {
            inRange: { color: Object.values(SEQUENTIAL_BLUE).slice(0, 8) },
            textStyle: { color: c.ink3, fontFamily: FONT.sans },
        },
        dataZoom: {
            borderColor: c.grid,
            fillerColor: mode === 'light' ? 'rgba(48,167,181,0.12)' : 'rgba(69,185,198,0.16)',
            handleStyle: { color: mode === 'light' ? '#FFFFFF' : '#123459', borderColor: c.axis },
            textStyle: { color: c.ink3 },
            dataBackground: { lineStyle: { color: c.axis }, areaStyle: { color: c.grid } },
        },
        markLine: { lineStyle: { color: c.ink3, type: 'dashed' }, label: { color: c.ink2 } },
    };
}
export const databunkerLight = build('light');
export const databunkerDark = build('dark');
let registered = false;
export function registerDataBunkerThemes(lib = echarts) {
    if (registered)
        return;
    lib.registerTheme('databunker-light', databunkerLight);
    lib.registerTheme('databunker-dark', databunkerDark);
    registered = true;
}
